import Pagination from '../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FiMoreVertical } from 'react-icons/fi';
import Modal from '../../components/shared/modal';
import EditModal from './edit-modal';
import ConfirmationModal from '../../components/shared/confirmation-modal';
import { formatDateTime } from '../../utils/format-date-time-utils';
import { statusColors } from '../../utils/status-color-utils';
import { RxCaretSort } from 'react-icons/rx';
import { RiArrowUpSLine } from 'react-icons/ri';
import ResetPassModal from './reset-pass-modal';
import { deleteUser, updateUserStatus } from '../../services/users-service';
import Loader from '../../components/shared/loader';
import UpdateStatusModal from '../../components/shared/update-status-modal';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import AuditLogModal from '../../components/shared/AuditLogModal';

const UserTable = ({ data, setData, roles, setSelectedRows, fetchGlobalData, loadingGlobalData, selectedRows, globalFilter, onGlobalFilterChange, newlyAddedItem, setNewlyAddedItem, onClearNewlyAddedItem, fetchUsers, loadingData, accountId, statusOptions }) => {
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openResetPass, setOpenResetPass] = useState(false);
  const [isFromAdding, setIsFromAdding] = useState(false);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);

  console.log(statusOptions);

  useEffect(() => {
    if (newlyAddedItem) {
      setSelectedRow(newlyAddedItem);
      setIsEditModalOpen(true);
      setNewlyAddedItem(null);
      setIsFromAdding(true);
    }
  }, [newlyAddedItem, setNewlyAddedItem]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleActionClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
    fetchGlobalData();
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (!e.target.closest('input[type="checkbox"]')) {
      setSelectedRow(row.original);
      setIsEditModalOpen(true);
      setIsFromAdding(false);
      fetchGlobalData();
    }
  };

  const handleConfirmAction = (action, row) => {
    setSelectedRow(row.original);
    if (action === 'Change Status') {
      setIsUpdateStatusModalOpen(true);
    } else {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    }
    setErrorMessage(null);
    closeMenu();
  };

  const handleOpenResetPass = (row) => {
    setSelectedRow(row.original);
    setOpenResetPass(true);
  };

  const handleCloseResetPass = () => {
    setOpenResetPass(false);
  };

  const handleConfirm = async () => {
    if (!selectedRow || !confirmAction) return;

    if (confirmAction === 'delete') {
      // try {
      //   setIsDeleting(true);
      //   const userId = selectedRow.userId;
      //   const res = await deleteUser(accountId, userId);

      //   if (res.status === 204) {
      //     setData(prevData => prevData.filter(item => item.userId !== userId));
      //     setIsConfirmModalOpen(false);
      //     setSelectedRow(null);
      //     setConfirmAction(null);
      //     setSuccessMessage('User deleted successfully!');
      //   } else {
      //     setErrorMessage(res.data?.message || 'Failed to delete user');
      //   }
      // } catch (error) {
      //   console.error('Error deleting user:', error);
      //   setErrorMessage('An error occurred while deleting the user');
      // } finally {
      //   setIsDeleting(false);
      // }
    } else if (confirmAction === 'approved') {
      // Placeholder for approve action; implement if needed
      console.log('Approving user:', selectedRow);
      setIsConfirmModalOpen(false);
      setSuccessMessage('User approved successfully!');
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                row.getToggleSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        enableSorting: false,
        size: 50,
        maxSize: 50,
      },
      {
        accessorKey: 'userId',
        header: 'User ID',
        enableSorting: true,
        size: 80,
        maxSize: 100,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        enableSorting: true,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        enableSorting: true,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'username',
        header: 'Username',
        enableSorting: true,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
        size: 180,
        maxSize: 200,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'role',
        header: 'Role',
        enableSorting: false,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{camelCaseToWords(info.getValue()) || '----'}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        size: 100,
        maxSize: 120,
        cell: (info) => {
          const rawStatus = info.getValue();
          const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : 'unknown';
          const color = statusColors[status] || statusColors.default;
          const displayStatus = camelCaseToWords(info.getValue());
          return (
            <div className="flex justify-start">
              <span
                className="py-0.5 rounded-md text-sm font-medium capitalize text-center truncate"
                style={{
                  backgroundColor: color,
                  color: 'black',
                  border: `1px solid ${color}`,
                  width: '100px',
                  display: 'inline-block',
                  textAlign: 'center',
                }}
              >
                {displayStatus}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const rowId = row.id;
          return (
            <div className=" flex justify-center w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(rowId);
                }}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <FiMoreVertical className="text-gray-600 text-sm" />
              </button>
              {openMenuId === rowId && (
                <div
                  className="absolute right-0 w-32 bg-white rounded-md shadow-md z-10 border border-gray-200 top-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Change Status', row)}
                  >
                    Change Status
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                    onClick={() => handleOpenResetPass(row)}
                  >
                    Reset Password
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedRow(row.original);
                      setIsAuditLogModalOpen(true);
                      closeMenu();
                    }}
                  >
                    Audit Log
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 50,
        // maxSize: 50,
      },
    ],
    [openMenuId]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      rowSelection: selectedRows,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onRowSelectionChange: setSelectedRows,
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      const rowValue = String(row.getValue(columnId)).toLowerCase();
      return rowValue.includes(searchLower);
    },
  });

  return (
    <div className="w-full">
      {successMessage && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
          {successMessage}
        </div>
      )}

      <div className="overflow-x-auto max-w-full pb-20">
        {loadingData ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm border border-gray-200" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '50px', maxWidth: '50px' }} />
              <col style={{ width: '80px', maxWidth: '100px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '180px', maxWidth: '200px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '100px', maxWidth: '120px' }} />
              <col style={{ width: '50px', maxWidth: '50px' }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-2 py-1.5 border-b capitalize truncate"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        width: header.column.getSize(),
                        maxWidth: header.column.columnDef.maxSize,
                      }}
                    >
                      <div className="flex items-center gap-1 truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-black">
                            {header.column.getIsSorted() === 'asc' ? (
                              <RiArrowUpSLine className="text-base mt-0.5" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <RiArrowUpSLine className="text-base rotate-180" />
                            ) : (
                              <RxCaretSort className="text-lg" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table
                .getRowModel()
                .rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 relative cursor-pointer"
                    onClick={(e) => handleRowClick(row, e)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-2 py-1 border-b truncate"
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isEditModalOpen && selectedRow}
        onClose={() => {
          setIsEditModalOpen(false);
          onClearNewlyAddedItem();
        }}
        modalClass="xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%]"
      >
        <EditModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setIsEditModalOpen={setIsEditModalOpen}
          setData={setData}
          fetchUsers={fetchUsers}
          accountId={accountId}
          fromAdding={isFromAdding}
          loadingGlobalData={loadingGlobalData}
          fetchGlobalData={fetchGlobalData}
          roles={roles}
        />
      </Modal>

      <Modal
        isOpen={isUpdateStatusModalOpen && selectedRow}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:!w-[25%] lg:!w-[28%] md:!w-[35%] sm:!w-[45%] !w-[90%] !px-0"
      >
        <UpdateStatusModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setData={setData}
          setIsUpdateStatusModalOpen={setIsUpdateStatusModalOpen}
          fetchData={fetchUsers}
          accountId={accountId}
          modalTitle="Change User Status"
          statusKey="status"
          statusOptions={statusOptions}
          updateStatusApi={updateUserStatus}
          uniqueIdKey="userId"
        />
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen && selectedRow}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setErrorMessage(null);
          setSelectedRow(null);
        }}
        modalClass="xl:!w-[25%] lg:w-[32%] md:w-[40%] sm:w-[60%] w-[90%]"
      >
        <ConfirmationModal
          confirmAction={confirmAction}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          handleConfirm={handleConfirm}
          loading={isDeleting}
          errorMessage={errorMessage}
        />
      </Modal>

      <Modal
        isOpen={openResetPass}
        onClose={handleCloseResetPass}
        modalClass="xl:!w-[28%] lg:!w-[34%] md:!w-[40%] sm:w-[60%] w-[90%]"
      >
        <ResetPassModal
          close={handleCloseResetPass}
          email={selectedRow?.email || ''}
          accountId={accountId}
          userId={selectedRow?.userId}
        />
      </Modal>

      <Modal
        isOpen={isAuditLogModalOpen && selectedRow}
        onClose={() => {
          setIsAuditLogModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
      >
        <AuditLogModal
          isOpen={isAuditLogModalOpen}
          onClose={() => {
            setIsAuditLogModalOpen(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          linkedType="user"
          linkedId={selectedRow?.userId}
        />
      </Modal>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div >
  );
};

export default UserTable;